import {useCallback, useContext, useEffect, useState} from 'react';

import {ConfirmationContext} from './confirmation-context';

import type {ConfirmationContextType} from './confirmation-context';

type Props<T> = {
    chunkName: string,
    onProceed: (params?: T) => void,
};

export const useConfirmationChunk = <T, >({chunkName, onProceed}: Props<T>) => {
    const [proceededData, setProceededData] = useState<T | undefined>(undefined);
    const [isChunkChanged, setIsChunkChanged] = useState(false);

    const {
        changedChunks,
        onShowModal,
        isChunkChanged: checkChunk,
        addChangedChunk,
        removeChangedChunk,
    } = useContext<ConfirmationContextType>(ConfirmationContext);

    useEffect(() => {
        (isChunkChanged ? addChangedChunk : removeChangedChunk)(chunkName);
    }, [isChunkChanged, chunkName, addChangedChunk, removeChangedChunk]);

    useEffect(() => {
        if (proceededData && !checkChunk(chunkName)) {
            onProceed(proceededData);
            setProceededData(undefined);
        }
    }, [proceededData, checkChunk, onProceed, chunkName]);

    const proceed = useCallback((params?: T) => {
        const emptyValue = true as T;
        setProceededData(params || emptyValue);
    }, []);

    const isSomeChunkChanged = useCallback((comparator: (chunk: string) => boolean) => {
        return changedChunks.some(comparator);
    }, [changedChunks]);

    return {
        isSomeChunkChanged,
        onShowModal,
        isCurrentChunkChanged: checkChunk(chunkName),
        proceed,
        setIsChunkChanged,
    };
};
